"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ButtonBase = exports.defaultShadows = exports.paddings = void 0;
const react_1 = __importDefault(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const lodash_merge_1 = __importDefault(require("lodash.merge"));
const polished_1 = require("polished");
const create_use_styles_1 = __importDefault(require("../../theming/create-use-styles"));
const color_1 = require("../../theming/utils/color");
function colorStyle(color, variant, { colors, colorScheme, outline, shadow }) {
    color = colors[color] || color;
    const desaturated = (color) => (0, color_1.mix)(colors.background, (0, color_1.desaturate)(color, 0.3), 0.6);
    const faded = (0, color_1.fade)(color, colorScheme === "light" ? 0.03 : 0.075);
    const shaded = (0, color_1.mix)(color, colors.text, 0.15);
    const tinted0 = (color) => (0, color_1.mix)(colors.background, color, colorScheme === "light" ? 0.03 : 0.075);
    const tinted10 = (color) => (0, color_1.mix)(colors.background, color, 0.1);
    const tinted15 = (color) => (0, color_1.mix)(colors.background, color, 0.15);
    const tinted50 = (color) => (0, color_1.mix)(colors.background, color, 0.5);
    const tinted85 = (color) => (0, color_1.mix)(colors.background, color, 0.85);
    const style = {
        filled: {
            "color": (0, color_1.contrast)(color),
            "backgroundColor": color,
            "&:hover:not(:disabled)": {
                color: tinted85((0, color_1.contrast)(color)),
                backgroundColor: tinted85(color),
            },
            "&:focus:not(:disabled)": {
                boxShadow: outline(color),
            },
            "&:active": {
                color: (0, color_1.contrast)(color),
                backgroundColor: shaded,
            },
            "&:disabled": {
                color: tinted50((0, color_1.contrast)(color)),
                backgroundColor: desaturated(color),
            },
        },
        outline: {
            color,
            "backgroundColor": "transparent",
            "borderColor": color,
            "&:hover:not(:disabled)": {
                color: tinted85(color),
                backgroundColor: faded,
                borderColor: tinted85(color),
            },
            "&:focus:not(:disabled)": {
                boxShadow: outline(color),
            },
            "&:active": {
                color: shaded,
                backgroundColor: "transparent",
                borderColor: shaded,
            },
            "&:disabled": {
                color: desaturated(color),
                borderColor: desaturated(color),
            },
        },
        link: {
            color,
            "backgroundColor": "transparent",
            "&:hover:not(:disabled)": {
                color: tinted85(color),
                backgroundColor: faded,
            },
            "&:focus:not(:disabled)": {
                boxShadow: outline(color),
            },
            "&:active": {
                color: shaded,
                backgroundColor: "transparent",
            },
            "&:disabled": {
                color: desaturated(color),
            },
        },
        light: {
            color,
            "backgroundColor": colors.background,
            "&:hover:not(:disabled)": {
                color: tinted85(color),
                backgroundColor: tinted0(color),
            },
            "&:focus:not(:disabled)": {
                boxShadow: outline(color),
            },
            "&:active": {
                color: shaded,
                backgroundColor: colors.background,
            },
            "&:active:not(:disabled)": {
                boxShadow: shadow("md"),
            },
            "&:disabled": {
                color: desaturated(color),
            },
        },
        faded: {
            color,
            "backgroundColor": tinted10(color),
            "&:hover:not(:disabled)": {
                color: tinted85(color),
                backgroundColor: tinted15(color),
            },
            "&:focus:not(:disabled)": {
                boxShadow: outline(color),
            },
            "&:active": {
                color: shaded,
                backgroundColor: tinted0(color),
            },
            "&:disabled": {
                color: desaturated(color),
                backgroundColor: desaturated(tinted10(color)),
            },
        },
    };
    return style[variant];
}
exports.paddings = {
    small: { x: 12, y: "xs" },
    medium: { x: "md", y: "sm" },
    large: { x: 20, y: 12 },
};
function paddingStyle(size, borderWidth, { spacing }) {
    return {
        padding: spacing(exports.paddings[size].y, exports.paddings[size].x)
            .split(" ")
            .map(paddingSize => `${(0, polished_1.math)(`${paddingSize} - ${borderWidth}`)}`)
            .join(" "),
    };
}
const useStyles = (0, create_use_styles_1.default)(theme => {
    const { typography } = theme;
    return {
        button: ({ color, shadow, size, variant }) => (0, lodash_merge_1.default)({
            "display": "inline-flex",
            "alignItems": "center",
            "maxWidth": "100%",
            "fontFamily": "inherit",
            "fontSize": typography.body.fontSize,
            "fontWeight": typography.fontWeights.medium,
            "lineHeight": typography.body.lineHeight,
            "verticalAlign": "middle",
            "border": `${theme.size(1)} solid`,
            "borderColor": "transparent",
            "borderRadius": theme.size(4),
            "userSelect": "none",
            "&:not(:disabled)": {
                boxShadow: shadow ? theme.shadow("sm") : "none",
                cursor: "pointer",
            },
            "&:hover, &:focus": {
                textDecoration: "none",
            },
            "&:focus": {
                outline: 0,
            },
            "& > span": {
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                pointerEvents: "none",
            },
        }, colorStyle(color, variant, theme), paddingStyle(size, theme.size(1), theme)),
    };
});
exports.defaultShadows = {
    filled: true,
    outline: false,
    link: false,
    light: true,
    faded: true,
};
exports.ButtonBase = react_1.default.forwardRef(function ButtonBase({ children, className, color = "primary", size = "medium", variant = "filled", shadow = exports.defaultShadows[variant || "filled"], ...props }, ref) {
    const classes = useStyles({ color, shadow, size, variant });
    return (react_1.default.createElement("button", { ref: ref, className: (0, classnames_1.default)(classes.button, className), ...props }, children));
});
exports.default = exports.ButtonBase;
//# sourceMappingURL=data:application/json;base64,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